import React from 'react';

import noop from 'lodash/noop';

import {
  ActionButton,
  Button,
  EButtonAppearance,
  EButtonSize,
  TActionButtonOption,
  TButtonProps,
} from '@edna/components';
import {TPlacementX} from '@edna/components/types';

import {PermissionCheck} from './PermissionCheck';
import {EActionType} from './constants';
import * as S from './style';
import {TPermissionProps} from './types';

export type TActionType = Lowercase<keyof typeof EActionType>;

type TProps = React.ButtonHTMLAttributes<HTMLButtonElement> & {
  // Permission
  permission: React.FC<TPermissionProps>;
  href?: string;
  // Button
  children: React.ReactNode;
  appearance?: keyof typeof EButtonAppearance;
  className?: string;
  size?: TButtonProps['size'];
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  dataSelector?: string;
  disabled?: boolean;
  // ActionButton
  options?: TActionButtonOption[];
  overlayPlacementX?: TPlacementX;
};

const PermissionGranted = React.memo<TPermissionProps>(({children}) => <>{children(true)}</>);

const PermissionDenied = React.memo<TPermissionProps>(({children}) => <>{children(false)}</>);

const AddButton = React.memo<TProps>(
  ({
    appearance = EButtonAppearance.primary,
    size = EButtonSize.M,
    onClick = noop,
    dataSelector = 'AddButton',
    children,
    href,
    permission: Permission,
    disabled,
    options,
    overlayPlacementX,
    ...props
  }) => {
    if (options !== undefined) {
      return (
        <Permission href={href}>
          {(isGranted) => (
            <ActionButton
              {...props}
              disabled={!isGranted || disabled}
              data-selector={dataSelector}
              appearance={appearance}
              size={size}
              onClick={onClick}
              options={options}
              overlayPlacementX={overlayPlacementX}
            >
              {children}
            </ActionButton>
          )}
        </Permission>
      );
    }

    return (
      <Permission href={href}>
        {(isGranted) => (
          <Button
            {...props}
            disabled={!isGranted || disabled}
            data-selector={dataSelector}
            appearance={appearance}
            size={size}
            onClick={onClick}
          >
            <S.Container>{children}</S.Container>
          </Button>
        )}
      </Permission>
    );
  },
);

AddButton.displayName = 'AddButton';

const AddButtonText = React.memo(
  ({prefix, children}: {children: React.ReactNode; prefix?: string}) => {
    if (prefix) {
      return <S.Text>{`${prefix} ${children}`}</S.Text>;
    }

    return <S.Text>{children}</S.Text>;
  },
);

AddButtonText.displayName = 'AddButtonText';

export const {AddIcon, MinusIcon} = S;
export {
  AddButton,
  AddButtonText,
  EActionType,
  PermissionGranted,
  PermissionDenied,
  PermissionCheck,
};
